class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (!facetForm) return;

    // Listen to checkbox / price input changes
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    // Sort buttons → set hidden sort_by then submit via AJAX
    facetForm.querySelectorAll('.facets-dropdown-sort-list button').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const value = btn.getAttribute('data-value') || btn.textContent.trim();
        const sortInput = facetForm.querySelector('input[name="sort_by"]');
        if (sortInput) sortInput.value = value;

        // Update active class immediately
        facetForm.querySelectorAll('.facets-dropdown-sort-list button').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');

        // Trigger AJAX submit
        this.onSubmitHandler({
          preventDefault: () => {},
          target: btn,
          srcElement: btn,
        });
      });
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove, facet-remove a').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    const countContainer = document.getElementById('ProductCount');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const grid = document.getElementById('ProductGridContainer');
    if (grid) {
      const collection = grid.querySelector('.collection');
      if (collection) collection.classList.add('loading');
    }
    if (countContainer) countContainer.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
      })
      .catch((err) => {
        console.error('Facets fetch error:', err);
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsed.getElementById('ProductGridContainer');
    const currentGrid = document.getElementById('ProductGridContainer');
    if (newGrid && currentGrid) {
      currentGrid.innerHTML = newGrid.innerHTML;
      currentGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    }
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newCount = parsed.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    if (newCount && container) {
      container.innerHTML = newCount.innerHTML;
      container.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  /**
   * Custom renderFilters for the dropdown layout.
   * We replace the entire facets-container so filter columns, pills,
   * breadcrumb and product count all stay in sync — but we preserve
   * the open/closed state of the dropdown panel.
   */
  static renderFilters(html, event) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newContainer = parsed.getElementById('FacetsDropdownContainer');
    const currentContainer = document.getElementById('FacetsDropdownContainer');
    if (!newContainer || !currentContainer) return;

    // Remember whether panel was open and which input was focused
    const wasOpen = currentContainer
      .querySelector('#FacetsDropdownPanel')
      ?.classList.contains('facets-dropdown-panel--open');
    const focusedName = document.activeElement && document.activeElement.name;
    const focusedValue = document.activeElement && document.activeElement.value;

    // Replace the inner HTML — keeps the wrapper element & its listeners on document
    currentContainer.innerHTML = newContainer.innerHTML;

    // Restore open state
    if (wasOpen) {
      const panel = currentContainer.querySelector('#FacetsDropdownPanel');
      const bar = currentContainer.querySelector('#FacetsDropdownBar');
      const toggle = currentContainer.querySelector('#FacetsDropdownToggle');
      const icon = currentContainer.querySelector('#FacetsToggleIcon');
      if (panel) panel.classList.add('facets-dropdown-panel--open');
      if (bar) bar.classList.add('facets-dropdown-bar--open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (icon) icon.textContent = '−';
    }

    // Re-bind dropdown UI (toggle, outside-click, etc.)
    if (typeof window.initFacetsDropdown === 'function') {
      window.initFacetsDropdown();
    }

    // Restore focus to the input the user just clicked (so keyboard users don't lose place)
    if (focusedName) {
      const sel = `input[name="${focusedName}"]${focusedValue ? `[value="${focusedValue}"]` : ''}`;
      const restored = currentContainer.querySelector(sel);
      if (restored) restored.focus();
    }

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    if (!grid || !grid.dataset.id) {
      // Fallback: try to read from ProductGridContainer
      const container = document.getElementById('ProductGridContainer');
      const fallbackId = container?.querySelector('[data-id]')?.dataset.id;
      return fallbackId ? [{ section: fallbackId }] : [];
    }
    return [{ section: grid.dataset.id }];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();

    const form = this.querySelector('form#FacetFiltersForm');
    if (!form) return;

    const searchParams = this.createSearchParams(form);
    this.onSubmitForm(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* ---------------- Price Range ---------------- */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    if (inputs.length < 2) return;
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

/* ---------------- Facet Remove (active pills) ---------------- */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form && typeof form.onActiveFilterClick === 'function') {
      form.onActiveFilterClick(event);
    }
  }
}
customElements.define('facet-remove', FacetRemove);